import React from 'react';
import { Filter, X, CalendarPlus, CalendarClock, ArrowUpDown } from 'lucide-react';
import {
  LICENSE_STATUSES, LICENSE_SORT_OPTIONS, DEFAULT_LICENSE_FILTERS, DEFAULT_LICENSE_SORT,
  countActiveFilters, parseSortValue,
} from '../../../utils/adminLicenseFilters';

const STATUS_META = {
  active: { label: 'Actives', on: 'bg-money/15 text-money border-money/40' },
  expired: { label: 'Expirées', on: 'bg-danger/10 text-danger border-danger/40' },
  disabled: { label: 'Désactivées', on: 'bg-ink-700/60 text-slate-300 border-slate-500/40' },
};

/**
 * Admin — Licences : filtres statut / dates + tri (21/09/2026).
 * Purement présentationnel : l'état vit dans AdminPanel.
 */
export default function LicenseFilterBar({ filters, setFilters, sort, setSort, statusCounts }) {
  const activeCount = countActiveFilters(filters);
  const isDefaultSort = sort.key === DEFAULT_LICENSE_SORT.key && sort.dir === DEFAULT_LICENSE_SORT.dir;
  const patch = (p) => setFilters((prev) => ({ ...prev, ...p }));
  const toggleStatus = (s) => {
    const cur = filters.statuses || [];
    patch({ statuses: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] });
  };
  const reset = () => { setFilters(DEFAULT_LICENSE_FILTERS); setSort(DEFAULT_LICENSE_SORT); };

  return (
    <div className="bg-ink-800 border border-ink-600 rounded-xl px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-3" data-testid="license-filter-bar">
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-slate-500" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Statut</span>
        <div className="flex items-center gap-1">
          {LICENSE_STATUSES.map((s) => {
            const on = (filters.statuses || []).includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                aria-pressed={on}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  on ? STATUS_META[s].on : 'border-ink-600 text-slate-500 hover:text-slate-200'
                }`}
                data-testid={`license-status-filter-${s}`}
              >
                {STATUS_META[s].label} ({statusCounts[s] || 0})
              </button>
            );
          })}
        </div>
      </div>

      <DateRange
        icon={CalendarPlus} label="Créée entre" idPrefix="created"
        from={filters.createdFrom} to={filters.createdTo}
        onFrom={(v) => patch({ createdFrom: v })} onTo={(v) => patch({ createdTo: v })}
      />
      <DateRange
        icon={CalendarClock} label="Expire entre" idPrefix="expires"
        from={filters.expiresFrom} to={filters.expiresTo}
        onFrom={(v) => patch({ expiresFrom: v })} onTo={(v) => patch({ expiresTo: v })}
      />

      <div className="flex items-center gap-2 ml-auto">
        <ArrowUpDown className="w-4 h-4 text-slate-500" />
        <select
          value={`${sort.key}:${sort.dir}`}
          onChange={(e) => setSort(parseSortValue(e.target.value))}
          className="bg-background text-foreground border border-ink-600 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-accent"
          data-testid="license-sort-select"
        >
          {LICENSE_SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {(activeCount > 0 || !isDefaultSort) && (
          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-foreground px-2 py-1.5 rounded-lg border border-ink-600 hover:bg-ink-700/40"
            data-testid="license-filters-reset"
          >
            <X className="w-3.5 h-3.5" /> Réinitialiser{activeCount > 0 ? ` (${activeCount})` : ''}
          </button>
        )}
      </div>
    </div>
  );
}

function DateRange({ icon: Icon, label, idPrefix, from, to, onFrom, onTo }) {
  const cls = 'bg-background text-foreground border border-ink-600 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-accent [color-scheme:dark]';
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-slate-500" />
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <input type="date" value={from || ''} onChange={(e) => onFrom(e.target.value)} className={cls} data-testid={`license-${idPrefix}-from`} />
      <span className="text-xs text-slate-600">→</span>
      <input type="date" value={to || ''} onChange={(e) => onTo(e.target.value)} className={cls} data-testid={`license-${idPrefix}-to`} />
    </div>
  );
}
